import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, withCtx as _withCtx, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, createVNode as _createVNode, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = ["onClick"]
const _hoisted_2 = { style: {"position":"relative"} }
const _hoisted_3 = {
  key: 0,
  class: "ellipsis-dropdown"
}
const _hoisted_4 = ["onClick", "onMouseenter"]
const _hoisted_5 = {
  key: 2,
  class: "breadcrumbs__separator"
}

import { ref, onMounted, onBeforeUnmount, nextTick, computed, toRefs, watch } from 'vue'
import type { BreadcrumbOption } from '@/utils/breadcrumbs'
import ButtonText from './buttons/ButtonText.vue'
import TextBase from './TextBase.vue'

interface ItemNode {
  kind: 'item'
  option: BreadcrumbOption
  key: string | number
}
interface EllipsisNode {
  kind: 'ellipsis'
  hidden: BreadcrumbOption[]
  key: 'ellipsis'
}
type CrumbNode = ItemNode | EllipsisNode


export default /*@__PURE__*/_defineComponent({
  __name: 'BreadcrumbBase',
  props: {
    breadcrumbOptions: {}
  },
  emits: ["onClicked"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const { breadcrumbOptions } = toRefs(props)
const emits = __emit
const isSingle = computed(() => breadcrumbOptions.value.length === 1)
const containerRef = ref<HTMLElement | null>(null)
const hiddenCount = ref(0)
const showEllipsisDropdown = ref(false)
const ellipsisWrapperRef = ref<HTMLElement[]>([])
const itemTextRefs = ref<Map<string | number, HTMLElement | null>>(new Map())
const calculatedTextMaxWidths = ref<Map<string | number, string>>(new Map())
const hoveredItemId = ref<string | number | null>(null)

let previousContainerWidth = 0
let ro: ResizeObserver | undefined

watch(
  breadcrumbOptions,
  () => {
    hiddenCount.value = 0
    previousContainerWidth = 0
    itemTextRefs.value.clear()
    calculatedTextMaxWidths.value.clear()
    nextTick(measure)
  },
  { deep: true }
)

onMounted(() => {
  if (!containerRef.value) return
  ro = new ResizeObserver((entries) => {
    const entry = entries[0]
    if (entry && entry.contentRect.width !== previousContainerWidth) {
      // Only run measure if width actually changed.
      // We reset previousContainerWidth inside measure AFTER deciding if hiddenCount needs to change
      // or if it's time to recalculate text widths.
      nextTick(measure)
    }
  })
  ro.observe(containerRef.value)
  nextTick(measure)
})

onBeforeUnmount(() => {
  if (ro) {
    ro.disconnect()
  }
})
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
const ensureKey = (option: BreadcrumbOption, index: number, prefix: string = '') =>
  option.id ?? `${prefix}${index}`

const visibleItems = computed<CrumbNode[]>(() => {
  const opts = breadcrumbOptions.value
  const total = opts.length
  const hc = hiddenCount.value

  if (total === 0) return []

  if (total <= 3) {
    return opts.map((o, i) => ({ kind: 'item', option: o, key: ensureKey(o, i, 'all') }))
  }
  if (hc === 0) {
    return opts.map((o, i) => ({ kind: 'item', option: o, key: ensureKey(o, i, 'all_hc0') }))
  }

  const maxHideActual = total - 3
  if (hc >= maxHideActual && total > 3) {
    const hiddenAll = opts.slice(2, total - 1)
    return [
      { kind: 'item', option: opts[0], key: ensureKey(opts[0], 0, 'fh_') },
      { kind: 'item', option: opts[1], key: ensureKey(opts[1], 1, 'fh_') },
      { kind: 'ellipsis', hidden: hiddenAll, key: 'ellipsis' },
      { kind: 'item', option: opts[total - 1], key: ensureKey(opts[total - 1], total - 1, 'fh_') }
    ]
  }

  const hidden = opts.slice(2, 2 + hc)
  const tail = opts.slice(2 + hc)
  const result: CrumbNode[] = [
    { kind: 'item', option: opts[0], key: ensureKey(opts[0], 0, 'ph_') },
    { kind: 'item', option: opts[1], key: ensureKey(opts[1], 1, 'ph_') },
    { kind: 'ellipsis', hidden, key: 'ellipsis' }
  ]
  tail.forEach((o, i) =>
    result.push({ kind: 'item', option: o, key: ensureKey(o, i + 2 + hc, 'ph_tail_') })
  )
  return result
})

function onClicked(option: BreadcrumbOption) {
  emits('onClicked', option)
}

function isLastItem(index: number): boolean {
  return index === visibleItems.value.length - 1
}

function measure() {
  const el = containerRef.value
  if (!el || breadcrumbOptions.value.length === 0) {
    calculatedTextMaxWidths.value.clear()
    previousContainerWidth = el ? el.clientWidth : 0
    return
  }

  const containerClientWidth = el.clientWidth
  const currentHiddenCount = hiddenCount.value

  // Temporarily remove maxWidth constraints to measure natural scrollWidth
  const oldMaxWidths = new Map(calculatedTextMaxWidths.value)
  calculatedTextMaxWidths.value.clear()

  nextTick(() => {
    if (!containerRef.value) {
      // Component might have unmounted
      calculatedTextMaxWidths.value = oldMaxWidths // Restore if unmounted during tick
      return
    }

    let newHiddenCount = currentHiddenCount
    const scrollWidth = el.scrollWidth
    const totalOptions = breadcrumbOptions.value.length
    const maxPossibleToHide = Math.max(0, totalOptions - 3)

    if (containerClientWidth < previousContainerWidth && currentHiddenCount === 0) {
      // If width decreased and nothing is hidden, we might need to hide something
      if (scrollWidth > containerClientWidth && newHiddenCount < maxPossibleToHide) {
        newHiddenCount++
      }
    } else if (containerClientWidth > previousContainerWidth && currentHiddenCount > 0) {
      // If container grew and items are hidden, try to show more by resetting hiddenCount
      // This will trigger a re-evaluation of hiddenCount based on new scrollWidth
      newHiddenCount = 0
    } else if (scrollWidth > containerClientWidth) {
      // Standard overflow case: try to hide more if possible
      if (newHiddenCount < maxPossibleToHide) {
        newHiddenCount++
      }
    }
    // If scrollWidth <= containerClientWidth and newHiddenCount === 0, we are good.
    // If scrollWidth <= containerClientWidth and newHiddenCount > 0,
    // it means we might have space to unhide. The "container grew" condition handles this.

    previousContainerWidth = containerClientWidth // Update after comparisons

    if (newHiddenCount !== currentHiddenCount) {
      hiddenCount.value = newHiddenCount
      // `hiddenCount` change will trigger re-render and `visibleItems` update.
      // Then `measure` will be called again by the watcher on `visibleItems` (implicitly via DOM changes) or ResizeObserver.
      // We need to ensure `measure` is called again after DOM update from new `hiddenCount`.
      nextTick(measure) // Re-measure after hiddenCount change applies
      return
    }

    // If hiddenCount is stable, restore oldMaxWidths before recalculating
    calculatedTextMaxWidths.value = oldMaxWidths
    // Then, update text item properties (maxWidths and overflow flags)
    nextTick(() => {
      if (!containerRef.value) return
      updateTextItemProperties()
    })
  })
}

function updateTextItemProperties() {
  const el = containerRef.value
  if (!el || visibleItems.value.length === 0) {
    calculatedTextMaxWidths.value.clear()
    return
  }

  const containerTotalWidth = el.clientWidth
  let occupiedByNonText = 0
  const textItemKeysForDynamicWidth: (string | number)[] = []

  // Use current DOM elements for accurate width measurements
  const childrenElements = Array.from(el.children) as HTMLElement[]
  let childElIdx = 0

  visibleItems.value.forEach((node, vIdx) => {
    const itemWrapperElement = childrenElements[childElIdx++]
    if (!itemWrapperElement) return

    if (node.kind === 'item') {
      if (node.option.icon) {
        occupiedByNonText += itemWrapperElement.offsetWidth
      } else {
        textItemKeysForDynamicWidth.push(node.key)
      }
    } else {
      // Ellipsis node
      occupiedByNonText += itemWrapperElement.offsetWidth
    }

    if (vIdx < visibleItems.value.length - 1) {
      const separatorElement = childrenElements[childElIdx++]
      if (separatorElement) {
        occupiedByNonText += separatorElement.offsetWidth
      }
    }
  })

  const newCalculatedMaxWidths = new Map<string | number, string>()
  if (textItemKeysForDynamicWidth.length > 0) {
    const availableWidthForTextItems = Math.max(0, containerTotalWidth - occupiedByNonText)
    // Ensure a minimum sensible width for readability, especially if multiple text items.
    // e.g. if only one text item, it can take more space.
    const minSensibleWidthPerItem = textItemKeysForDynamicWidth.length === 1 ? 80 : 40
    const dynamicWidthPerTextItem = Math.max(
      minSensibleWidthPerItem,
      availableWidthForTextItems / textItemKeysForDynamicWidth.length
    )

    textItemKeysForDynamicWidth.forEach((key) => {
      newCalculatedMaxWidths.set(key, `${dynamicWidthPerTextItem}px`)
    })
  }
  calculatedTextMaxWidths.value = newCalculatedMaxWidths
}

function getTextItemCalculatedMaxWidth(itemKey: string | number): string | undefined {
  return calculatedTextMaxWidths.value.get(itemKey)
}
function toggleEllipsisDropdown() {
  showEllipsisDropdown.value = !showEllipsisDropdown.value
}
function selectHiddenOption(option: BreadcrumbOption) {
  emits('onClicked', option)
  showEllipsisDropdown.value = false
}
function handleClickOutside(event: MouseEvent) {
  const refs = ellipsisWrapperRef.value as HTMLElement[]

  const clickedInsideAny = refs.some((el) => {
    return el instanceof HTMLElement && el.contains(event.target as Node)
  })

  if (!clickedInsideAny && showEllipsisDropdown.value) {
    showEllipsisDropdown.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "containerRef",
    ref: containerRef,
    class: "breadcrumbs"
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleItems.value, (node, idx) => {
      return (_openBlock(), _createElementBlock(_Fragment, {
        key: node.key
      }, [
        (node.kind === 'item')
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["breadcrumbs__option", { 'is-last': idx === visibleItems.value.length - 1 }]),
              onClick: ($event: any) => (!isLastItem(idx) && onClicked(node.option))
            }, [
              (node.option.icon)
                ? (_openBlock(), _createBlock(ButtonText, {
                    key: 0,
                    class: _normalizeClass(['breadcrumbs__icon-item', isSingle.value ? 'no-hover' : '']),
                    buttonText: "",
                    size: "extra-small",
                    onOnClicked: ($event: any) => (!isLastItem(idx) && onClicked(node.option))
                  }, {
                    left: _withCtx(() => [
                      (_openBlock(), _createBlock(_resolveDynamicComponent(node.option.icon)))
                    ]),
                    _: 2
                  }, 1032, ["class", "onOnClicked"]))
                : (_openBlock(), _createBlock(TextBase, {
                    key: 1,
                    text: node.option.title,
                    class: "breadcrumbs__text",
                    type: "label",
                    style: _normalizeStyle({ maxWidth: getTextItemCalculatedMaxWidth(node.key) }),
                    overflow: "ellipsis",
                    "text-wrap": "nowrap",
                    "overflow-tooltip": ""
                  }, null, 8, ["text", "style"]))
            ], 10, _hoisted_1))
          : (_openBlock(), _createElementBlock("div", {
              key: 1,
              ref_for: true,
              ref_key: "ellipsisWrapperRef",
              ref: ellipsisWrapperRef,
              class: "breadcrumbs__option breadcrumbs__ellipsis-item"
            }, [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("p", {
                  class: "dot",
                  onClick: toggleEllipsisDropdown
                }, "…"),
                (showEllipsisDropdown.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      _createElementVNode("ul", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(node.hidden, (item) => {
                          return (_openBlock(), _createElementBlock("li", {
                            key: item.id ?? item.title,
                            onClick: ($event: any) => (selectHiddenOption(item)),
                            onMouseenter: ($event: any) => (hoveredItemId.value = item.id ?? item.title),
                            onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (hoveredItemId.value = null))
                          }, [
                            _createVNode(TextBase, {
                              text: item.title,
                              class: "breadcrumbs__text",
                              type: "label",
                              overflow: "ellipsis",
                              "text-wrap": "nowrap",
                              "overflow-tooltip": "",
                              color: hoveredItemId.value === (item.id ?? item.title) ? 'white' : 'black'
                            }, null, 8, ["text", "color"])
                          ], 40, _hoisted_4))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ])
            ], 512)),
        (idx < visibleItems.value.length - 1)
          ? (_openBlock(), _createElementBlock("span", _hoisted_5, [
              _renderSlot(_ctx.$slots, "default")
            ]))
          : _createCommentVNode("", true)
      ], 64))
    }), 128))
  ], 512))
}
}

})